import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@library/components/sections/Accordion';
import { Button, Tab } from '@library/components/buttons';
import { type MouseEvent, memo, useCallback, useState } from 'react';
import type { HierarchicalRulesetMetadata } from '@library/types/rulesets';
import { classNames } from '@library/utils';
import { getRulesetQualifiedPath } from '@library/component-utils/rulesets';

const activeStyle = 'bg-stankonia-400 rounded-xl';

const LeagueTreeItem = memo(
  ({
    ruleset,
    selectedQualifiedPath,
    onSelectRuleset,
    parentLevel,
    disabled
  }: {
    ruleset: HierarchicalRulesetMetadata;
    selectedQualifiedPath: string;
    onSelectRuleset: (rulesetPath: string) => void;
    parentLevel: number;
    disabled: boolean;
  }) => {
    const { attributes } = ruleset;
    const { name, children } = attributes;

    const path = getRulesetQualifiedPath(ruleset);

    const itemKey = `ruleset-path-${path}`;
    const level = parentLevel + 1;
    const paddingLeft = `pl-${level}`;

    const hasChildren =
      Array.isArray(children.data) && children.data.length > 0;

    const handleSelectRuleset = useCallback(
      (e: MouseEvent) => {
        e.preventDefault();
        onSelectRuleset(path);
      },
      [onSelectRuleset, path]
    );

    return (
      <AccordionItem value={itemKey} key={itemKey} isActive={!!ruleset.id}>
        <AccordionTrigger
          chevronPosition="left"
          chevronStyle={hasChildren ? 'chevron' : 'bullet'}
          labelAsTrigger={false}
          isActive={!!ruleset.id}
          className={classNames(
            selectedQualifiedPath === path ? activeStyle : ''
          )}
        >
          <Button
            type="button"
            className={classNames(
              'w-full text-start',
              ruleset.attributes.active
                ? 'text-soClean-100 hover:decoration-soClean-100 font-extrabold'
                : 'text-soClean-150 hover:decoration-soClean-150',
              ruleset.id ? '' : 'text-soFresh-160 hover:decoration-soFresh-160',
              disabled ? 'text-soClean-180' : ''
            )}
            onClick={handleSelectRuleset}
            disabled={disabled}
          >
            {name}
          </Button>
        </AccordionTrigger>
        {hasChildren && (
          <AccordionContent>
            <Accordion
              type="multiple"
              className={paddingLeft}
              // TODO: Collapsing all for now for easier navigation
              // defaultValue={children.data.map((c) => `ruleset-path-${c.path}`)}
              defaultValue={[]}
            >
              {children.data.map((childRuleset) => (
                <LeagueTreeItem
                  ruleset={childRuleset}
                  selectedQualifiedPath={selectedQualifiedPath}
                  onSelectRuleset={onSelectRuleset}
                  parentLevel={level}
                  key={`ruleset-path-${getRulesetQualifiedPath(childRuleset)}`}
                  disabled={disabled}
                />
              ))}
            </Accordion>
          </AccordionContent>
        )}
      </AccordionItem>
    );
  }
);

const LeagueTreeTabs = memo(
  ({
    selectedTab,
    onSelectTab,
    disabled
  }: {
    selectedTab: 'gameTypes' | 'gameModes';
    onSelectTab: (tabName: 'gameTypes' | 'gameModes') => void;
    disabled: boolean;
  }) => {
    const activeTabClassName =
      'border-accent bg-stankonia-100 text-soClean-100 mr-[1px]';
    const inactiveTabClassName =
      '-ml-px hover:bg-soFresh-120 disabled:bg-soFresh-150 disabled:cursor-not-allowed text-soClean-140 bg-soFresh-110 first-of-type:border-l-1';
    const alwaysTabClassName =
      'h-8 border border-soFresh-140 px-3 py-2 text-xs font-medium first-of-type:rounded-l last-of-type:rounded-r shrink-0 w-32';

    return (
      <div id="league-tree-tab-container" className="h-8 w-64">
        <Tab
          active
          handleClick={() => onSelectTab('gameTypes')}
          id="gameTypes-tab"
          size="small"
          tabName="Game Types"
          className={`${selectedTab === 'gameTypes' ? activeTabClassName : inactiveTabClassName} ${alwaysTabClassName}`}
          disabled={disabled}
        />
        <Tab
          active={false}
          handleClick={() => onSelectTab('gameModes')}
          id="gamemodes-tab"
          size="small"
          tabName="Lineup Types"
          className={`${selectedTab === 'gameModes' ? activeTabClassName : inactiveTabClassName} ${alwaysTabClassName}`}
          disabled={disabled}
        />
      </div>
    );
  }
);

export const LeagueTree = memo(
  ({
    globalRuleset,
    gameModesGlobalRulesets,
    customRuleset,
    selectedQualifiedPath,
    onSelectRuleset,
    disabled
  }: {
    globalRuleset?: HierarchicalRulesetMetadata; // eslint-disable-line react/require-default-props
    gameModesGlobalRulesets?: HierarchicalRulesetMetadata[]; // eslint-disable-line react/require-default-props
    customRuleset?: HierarchicalRulesetMetadata; // eslint-disable-line react/require-default-props
    selectedQualifiedPath: string;
    onSelectRuleset: (rulesetPath: string) => void;
    disabled: boolean;
  }) => {
    const [selectedTab, setSelectedTab] = useState<'gameTypes' | 'gameModes'>(
      'gameTypes'
    );

    return (
      <div id="rulesets-league-tree">
        {!customRuleset && (
          <LeagueTreeTabs
            selectedTab={selectedTab}
            onSelectTab={(tabName) => setSelectedTab(tabName)}
            disabled={disabled}
          />
        )}
        <div className="pl-4 pt-3">
          {customRuleset && (
            <Accordion
              type="multiple"
              className="w-56"
              defaultValue={[
                `ruleset-path-${getRulesetQualifiedPath(customRuleset)}`
              ]}
            >
              <LeagueTreeItem
                ruleset={customRuleset}
                selectedQualifiedPath={selectedQualifiedPath}
                onSelectRuleset={onSelectRuleset}
                parentLevel={1}
                disabled={disabled}
              />
            </Accordion>
          )}
          {selectedTab === 'gameTypes' && !customRuleset && (
            <Accordion
              type="multiple"
              className="w-56"
              defaultValue={[
                `ruleset-path-${getRulesetQualifiedPath(globalRuleset)}`
              ]}
            >
              <LeagueTreeItem
                ruleset={globalRuleset!}
                selectedQualifiedPath={selectedQualifiedPath}
                onSelectRuleset={onSelectRuleset}
                parentLevel={1}
                disabled={disabled}
              />
            </Accordion>
          )}
          {selectedTab === 'gameModes' && !customRuleset && (
            <Accordion
              type="multiple"
              className="w-56"
              // TODO: Collapsing all for now for easier navigation
              defaultValue={[]}
            >
              {gameModesGlobalRulesets?.map((ruleset) => (
                <LeagueTreeItem
                  ruleset={ruleset}
                  selectedQualifiedPath={selectedQualifiedPath}
                  onSelectRuleset={onSelectRuleset}
                  parentLevel={1}
                  key={getRulesetQualifiedPath(ruleset)}
                  disabled={disabled}
                />
              ))}
            </Accordion>
          )}
        </div>
      </div>
    );
  }
);
